import { debounce, isEmpty } from 'lodash';
import SmartySDK from 'smartystreets-javascript-sdk';

const ENHANCED_MATCH_NONE = 'none';
const ENHANCED_MATCH_VALID = 'postal-match';

export const NO_MATCH = 'no-match';
export const PARTIAL_MATCH = 'partial-match';
export const FULL_MATCH = 'full-match';

const API_CALL_DELAY = 1000;

const SmartyCore = SmartySDK.core;
const AddressLookup = SmartySDK.usAutocompletePro.Lookup;
const VerificationLookup = SmartySDK.usStreet.Lookup;

function buildClientBuilder(apiKey, license) {
  const credentials = new SmartyCore.SharedCredentials(apiKey);
  return new SmartyCore.ClientBuilder(credentials).withLicenses([license]);
}

export async function getAddressSuggestions(apiKey, input, selectedOption) {
  const clientBuilder = buildClientBuilder(apiKey, 'us-autocomplete-pro-cloud');
  const client = clientBuilder.buildUsAutocompleteProClient();

  const lookup = new AddressLookup(input);
  lookup.maxResults = 5;
  lookup.preferGeolocation = 'CITY';

  if (selectedOption) {
    const lineTwoText = ` ${selectedOption.secondary}`;
    const entriesText = ` (${selectedOption.entries})`;
    lookup.selected = `${selectedOption.streetLine}${lineTwoText}${entriesText}, ${selectedOption.city}, ${selectedOption.state}, ${selectedOption.zipcode}`;
  }

  let response = {};
  const lookupStr = JSON.stringify(lookup);
  try {
    response = await client.send(lookup);
    window.postHeapAnalytic('track', '[smarty][autocomplete][success]', {
      lookup: lookupStr,
      numberOfSuggestions: response.result?.length,
      isEntriesRequest: Boolean(selectedOption),
      originationId: window.AppConfig?.origination_id
    });
    return response;
  } catch (err) {
    window.postHeapAnalytic('track', '[smarty][autocomplete][failed]', {
      errorMessage: err.message,
      lookup: lookupStr,
      isEntriesRequest: Boolean(selectedOption),
      originationId: window.AppConfig?.origination_id
    });
    return response;
  }
}

export async function verifyAddress(
  apiKey,
  { lineOne, lineTwo, city, state, zip }
) {
  const clientBuilder = buildClientBuilder(apiKey, 'us-core-cloud');
  const client = clientBuilder.buildUsStreetApiClient();

  const lookup = new VerificationLookup();
  lookup.street = lineOne;
  lookup.secondary = lineTwo;
  lookup.city = city;
  lookup.state = state;
  lookup.zipCode = zip;
  lookup.match = 'enhanced';

  let result = {};
  const lookupStr = JSON.stringify(lookup);
  try {
    const response = await client.send(lookup);
    result = response?.lookups?.[0]?.result?.[0] || {};

    window.postHeapAnalytic('track', '[smarty][verification][success]', {
      originationId: window.AppConfig?.origination_id,
      lookup: lookupStr,
      components: JSON.stringify(result.components),
      metadata: JSON.stringify(result.metadata),
      analysis: JSON.stringify(result.analysis)
    });

    return result;
  } catch (err) {
    window.postHeapAnalytic('track', '[smarty][verification][failed]', {
      errorMessage: err.message,
      originationId: window.AppConfig?.origination_id,
      lookup: lookupStr
    });

    return result;
  }
}

export function addressMatchType(candidate) {
  const enhancedMatch = candidate?.analysis?.enhancedMatch;
  const footnotes = candidate?.analysis?.footnotes;

  if (
    !candidate ||
    isEmpty(candidate) ||
    enhancedMatch === ENHANCED_MATCH_NONE
  ) {
    return NO_MATCH;
  }

  if (enhancedMatch === ENHANCED_MATCH_VALID && !footnotes) {
    return FULL_MATCH;
  }

  return PARTIAL_MATCH;
}

export function getCandidateLineOne(candidate) {
  const components = candidate?.components || {};
  const primaryNumber = components.primaryNumber;
  const streetPredirection = components.streetPredirection;
  const streetName = components.streetName;
  const streetSuffix = components.streetSuffix;
  const streetPostdirection = components.streetPostdirection;

  const primaryNumberString = primaryNumber ? `${primaryNumber} ` : '';
  const streetPredirectionString = streetPredirection
    ? `${streetPredirection} `
    : '';
  const streetNameString = streetName ? `${streetName}` : '';
  const streetSuffixString = streetSuffix ? ` ${streetSuffix}` : '';
  const streetPostdirectionString = streetPostdirection
    ? ` ${streetPostdirection}`
    : '';

  return `${primaryNumberString}${streetPredirectionString}${streetNameString}${streetSuffixString}${streetPostdirectionString}`;
}

export function getCandidateLineTwo(candidate) {
  const components = candidate?.components || {};
  const secondaryDesignator = components.secondaryDesignator;
  const secondaryNumber = components.secondaryNumber;
  const extraSecondaryDesignator = components.extraSecondaryDesignator;
  const extraSecondaryNumber = components.extraSecondaryNumber;

  const secondaryDesignatorString = secondaryDesignator || '';
  const secondaryNumberString = secondaryNumber ? ` ${secondaryNumber}` : '';
  const extraSecondaryDesignatorString = extraSecondaryDesignator
    ? ` ${extraSecondaryDesignator}`
    : '';
  const extraSecondaryNumberString = extraSecondaryNumber
    ? ` ${extraSecondaryNumber}`
    : '';

  return `${secondaryDesignatorString}${secondaryNumberString}${extraSecondaryDesignatorString}${extraSecondaryNumberString}`;
}

export function getCandidateCity(candidate) {
  return candidate?.components?.cityName;
}

export function getCandidateState(candidate) {
  return candidate?.components?.state;
}

export function getCandidateZip(candidate) {
  return candidate?.components?.zipCode;
}

export function getCandidateAddress(candidate) {
  return {
    lineOne: getCandidateLineOne(candidate),
    lineTwo: getCandidateLineTwo(candidate),
    city: getCandidateCity(candidate),
    state: getCandidateState(candidate),
    zip: getCandidateZip(candidate)
  };
}

export async function callbackGetAddressSuggestions(
  apiKey,
  lineOneValue,
  selectedOption,
  callback
) {
  const results = await getAddressSuggestions(
    apiKey,
    lineOneValue,
    selectedOption
  );
  callback(results);
}

export const debouncedGetAddressSuggestions = debounce(
  callbackGetAddressSuggestions,
  API_CALL_DELAY
);
